import { Stack, Box, Typography, Grid, CircularProgress } from '@mui/material';

import React, { useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useLocation, useParams } from 'react-router-dom';

import moment from 'moment';
import { Controller, useForm } from 'react-hook-form';

import { createTask } from 'common/api';
import { IFiveWResponse } from 'common/api/type-def';
import {
	MODULES,
	MODULE_NAME,
	PAGINATION_CONSTANTS,
	TASKTYPE,
} from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { setShouldHorizonDetailRefresh } from 'common/store/reducer/horizonScanReducer';
import { setAllUsersList } from 'common/store/reducer/UserManagementListReducer';

import { getAllUsersList } from 'features/Settings/UserManagement/api';

import { IControlData, risklevel, taskStatus } from '../../mappingUtils';

import GrammarCheck from '../Overview/GrammarCheck';

import { infoIcon } from '../../../../../../common/assets/images';
import AccordionComp from '../../../../../../common/components/Accordion/AccordionComp';
import ButtonComp from '../../../../../../common/components/Button/ButtonComp';
import DatepickerComponent from '../../../../../../common/components/Datepicker/Datepicker';
import SelectDropdown, {
	ISelectOption,
} from '../../../../../../common/components/Select/SelectDropdown';
import TextFieldWithLabel from '../../../../../../common/components/TextFieldWithLabel/TextFieldWithLabel';
import {
	ENTITY_TYPE,
	RELATED_OBJECT_TYPE,
	OVERVIEW_TAB_LABEL,
} from '../../../../../../common/constants/TaxonomyConstants';
import { APIDateFormat } from '../../../../../../common/constants/TextContent';
import { RootState } from '../../../../../../common/store/AppStore';
import {
	setMappingDrawer,
	setToastMessage,
} from '../../../../../../common/store/reducer/commonReducer';
import {
	setGrammarCheckCompleted,
	setTabIndex,
	setTaxonomyDetailsTabIndex,
} from '../../../../../../common/store/reducer/mappingDetailsReducer';
import {
	getDateStringFromISOString,
	getISODateString,
	pascalFormat,
	scrollProps,
	toSentenceCase,
} from '../../../../../../common/utils/utilFunctions';
import { IAddUserResponse } from '../../../../../Settings/UserManagement/api/type-def';
import {
	createTaskUsingCIP,
	getEntityTaskData,
	getRemediationPlanTableData,
	getRelationshipTaskData,
	updateTask,
	updateTaskUsingCIP,
} from '../../../api';
import { ICreateUpdateTask } from '../../../api/type-def';

import TaskCard from './TaskCard';
import TaskComment from './TaskComment';

import taskStyle from './Tasks.module.scss';

const {
	HORIZON_SCAN_NAME,
	REQUIREMENT_INVENTORY_NAME,
	RISK_REFERENCE_TAXONOMY_NAME,
} = MODULE_NAME;

const {
	HORIZON_SCAN_AND_ALERTS,
	REQUIREMENT_INVENTORY,
	RISK_REFERENCE_TAXONOMY,
} = MODULES;

interface ITasks {
	data: IControlData[];
	objectType: string;
	objectId: string;
	validationData: IFiveWResponse | undefined | null;
	moduleType?: string;
	readOnly?: boolean;
}

const { OK, BAD_REQUEST, NOT_FOUND } = HTTP_STATUS_CODES;

const Tasks = (props: ITasks) => {
	const { data, objectType, objectId, validationData, readOnly, moduleType } =
		props;

	const location = useLocation();
	const { groupId, subPartId } = useParams();
	const citationId: string = location.state?.citationId || subPartId;
	const [entityOwnerId, setEntityOwnerId] = useState<string | null>(null);
	const [statusOptions, setStatusOptions] = useState(taskStatus);
	const [fiveWData, setFiveWdata] = useState<IFiveWResponse | undefined | null>(
		validationData,
	);
	const [paramsData] = useState({
		sortKey: '',
		pageSize: 500,
		pageNumber: PAGINATION_CONSTANTS.PAGE_NUMBER,
		sortDirection: '',
	});
	const dispatch = useDispatch();
	const [isSaving, setIsSaving] = useState(false);

	const sideDrawerState = useSelector(
		(state: RootState) => state.common.mappingDrawer,
	);
	const defaultTask = useSelector(
		(state: RootState) => state?.mappingDetails?.selectedTask,
	);

	const grammarCheckCompleted = useSelector(
		(state: RootState) => state?.mappingDetails?.grammarCheckCompleted,
	);

	const { usersList: users } = useSelector((state: any) => state.usersListmgmt);

	const selectedEntity = useSelector(
		(state: RootState) => state.common.selectedEntityTypeData,
	);
	const { id } = selectedEntity;

	const emptyTask = {
		id: '',
		taskName: '',
		taskDescription: '',
		controlDescription: fiveWData?.controlDescription || '',
		startDate: moment().format('MM/DD/YYYY'),
		dueDate: '',
		priorityLevel: '',
		taskOwner: entityOwnerId,
		assignedUserId: '',
		status: 'Not Started',
		relatedParentObjectType: ENTITY_TYPE.CITATION,
		relatedParentObjectId: citationId,
	} as ICreateUpdateTask;

	const setDefaultOwner = () => {
		if (data?.length) {
			const owner = data?.find((ele) => ele.label === 'Owner');
			if (owner?.value) {
				setEntityOwnerId(owner.value || null);
				setSelectedTask({ ...emptyTask, taskOwner: owner.value });
			}
		}
	};

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const [taskUsers, setTaskUsers] = useState<ISelectOption[]>([]);
	const [selectedTask, setSelectedTask] =
		useState<ICreateUpdateTask>(emptyTask);
	const [availableTask, setAvailableTask] = useState<ICreateUpdateTask[]>([]);
	const [expanded, setExpanded] = React.useState<string | false>('comments');

	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	/**
	 *  Get the user details
	 */
	const getAllUsers = async () => {
		const resp = await getAllUsersList(paramsData);
		if (resp.responseBody) {
			dispatch(setAllUsersList(resp?.responseBody?.content));
		}
	};

	/**
	 * Fetch the task list for the selected entity type
	 */
	const fetchEntityTaskList = async () => {
		setIsLoading(true);
		const response: any = await getEntityTaskData(objectType, objectId);
		if (response?.statusCode === OK) {
			const { responseBody } = response;
			const taskData: ICreateUpdateTask[] = [];
			responseBody?.forEach((element: any) => {
				const task: ICreateUpdateTask = {
					id: element.id,
					priorityLevel: element.priorityLevel,
					...element,
				};
				taskData.push(task);
			});
			setAvailableTask(taskData);
		}
		setIsLoading(false);
	};

	/**
	 * Fetch the task list for the selected object type
	 */

	const fetchTaskTable = async () => {
		const payload = {
			pageNumber: 0,
			pageSize: 10,
			sortField: 'targetEndDate',
			sortOrder: 'DESC',
			andCriterias: [
				{
					field: 'relatedParentObjectId',
					value: subPartId || '',
					operation: 'EQUAL',
				},
				{
					field: 'assignedObjectId',
					value: id || '',
					operation: 'EQUAL',
				},
			],
		};

		const response = await getRelationshipTaskData(payload);
		if (response?.statusCode === OK) {
			const taskData: ICreateUpdateTask[] = [];
			response?.responseBody?.records?.forEach((element: any) => {
				const task: ICreateUpdateTask = {
					id: element.id,
					dueDate: element.deadline,
					priorityLevel: element.risk,
					taskOwner: element.assignedOwnerId,
					...element,
				};
				taskData.push(task);
			});
			setAvailableTask(taskData);
		}
	};

	const fetchTaskList = async () => {
		setIsLoading(true);
		if (moduleType === HORIZON_SCAN_AND_ALERTS) {
			fetchTaskTable();
		} else {
			const response: any = await getRemediationPlanTableData(citationId);
			if (response?.statusCode === OK) {
				const { responseBody } = response;
				const taskData: ICreateUpdateTask[] = [];
				responseBody?.tasks?.forEach((element: any) => {
					if (
						element.assignedObjectId === objectId &&
						element.relatedParentObjectId === citationId
					) {
						const task: ICreateUpdateTask = {
							id: element.id,
							dueDate: element.deadline,
							priorityLevel: element.risk,
							taskOwner: element.assignedOwnerId,
							...element,
						};
						taskData.push(task);
					}
				});
				setAvailableTask(taskData);
			}
		}
		setIsLoading(false);
	};

	useEffect(() => {
		taskStatusOptionsSet();
		if (groupId) {
			fetchEntityTaskList();
		} else {
			fetchTaskList();
		}
		if (users.length === 0) {
			getAllUsers();
		}
		setDefaultOwner();
	}, []);

	useEffect(() => {
		if (defaultTask && availableTask.length) {
			const task = availableTask.filter((t) => t.id === defaultTask)[0];
			reset({ ...task });
			setSelectedTask(availableTask.filter((t) => t.id === defaultTask)[0]);
			taskStatusOptionsSet();
		}
	}, [defaultTask, availableTask]);

	useEffect(() => {
		if (selectedTask && selectedTask.fiveWDescription) {
			const taskFiveWData = {
				fiveWResponse: selectedTask.fiveWDescription,
				controlDescription: selectedTask.controlDescription || '',
				grammarScore: selectedTask.grammarScore || 0,
			} as IFiveWResponse;
			setFiveWdata(taskFiveWData);
		}
	}, [selectedTask]);

	const taskStatusOptionsSet = () => {
		setStatusOptions(taskStatus);
	};

	/**
	 * Task comment accordion change event
	 * @param panel panel name
	 * @returns void
	 */
	const handleAccordionChange =
		(panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
			setExpanded(isExpanded ? panel : false);
		};

	/**
	 * User list bind to dropdown for control owner and assigned to dropdown
	 */
	const formatTaskUsers = () => {
		const usersList: ISelectOption[] = users.map((ele: IAddUserResponse) => {
			const { firstName, lastName, middleInitial, userId } = ele;

			const displayName = `${pascalFormat(firstName?.toLowerCase())} ${
				middleInitial ? pascalFormat(middleInitial.toLowerCase()) : ''
			} ${pascalFormat(lastName?.toLowerCase())}`;
			return {
				label: displayName,
				value: userId?.toLowerCase(),
			} as ISelectOption;
		});
		return usersList;
	};

	useEffect(() => {
		setTaskUsers(formatTaskUsers());
	}, [users]);

	const {
		control,
		reset,
		trigger,
		getValues,
		setValue,
		formState: { isDirty, isValid },
	} = useForm<ICreateUpdateTask>({
		mode: 'onChange',
		defaultValues: selectedTask,
	});

	const onDateChangeHandler =
		(key: keyof ICreateUpdateTask) => (val: string | null) => {
			setValue(key, val ?? '', { shouldDirty: true });
			trigger(key);
		};

	/**
	 * On click of cancel for task empty the state
	 */
	const onCancelHandler = () => {
		reset({ ...emptyTask });
		setSelectedTask(emptyTask);
		dispatch(setGrammarCheckCompleted(false));
		taskStatusOptionsSet();
	};

	const getModuleName = () => {
		switch (moduleType) {
			case HORIZON_SCAN_AND_ALERTS:
				return HORIZON_SCAN_NAME;
			case REQUIREMENT_INVENTORY:
				return REQUIREMENT_INVENTORY_NAME;
			case RISK_REFERENCE_TAXONOMY:
				return RISK_REFERENCE_TAXONOMY_NAME;
			default:
				return '';
		}
	};

	const getRelatedParentObjectType = () => {
		switch (moduleType) {
			case HORIZON_SCAN_AND_ALERTS:
				return RELATED_OBJECT_TYPE.HORIZON;
			case REQUIREMENT_INVENTORY:
				return RELATED_OBJECT_TYPE.GROUP;
			default:
				return ENTITY_TYPE.CITATION;
		}
	};

	const updateTaskData = () => {
		const formData = getValues();
		return {
			...formData,
			dueDate: getISODateString(formData.dueDate),
			startDate: moment(formData.startDate).format(APIDateFormat),
			assignedObjectType:
				moduleType === HORIZON_SCAN_AND_ALERTS &&
				objectType === ENTITY_TYPE.OBLIGATION
					? RELATED_OBJECT_TYPE.GROUP
					: toSentenceCase(objectType),
			assignedObjectId: objectId,
			lastModifiedBy: loggedInUser?.userId,
			relatedParentObjectType: getRelatedParentObjectType(),
			relatedParentObjectId:
				moduleType === REQUIREMENT_INVENTORY ? groupId : citationId,
			grammarScore: fiveWData?.grammarScore.toString(),
			fiveWDescription: fiveWData?.fiveWResponse,
		} as ICreateUpdateTask;
	};

	const createTaskData = () => {
		const formData = getValues();
		const createTaskData: ICreateUpdateTask = {
			...formData,
			dueDate: getISODateString(formData.dueDate),
			startDate: moment().format(APIDateFormat),
			assignedObjectType:
				moduleType === HORIZON_SCAN_AND_ALERTS &&
				objectType === ENTITY_TYPE.OBLIGATION
					? RELATED_OBJECT_TYPE.GROUP
					: toSentenceCase(objectType),
			assignedObjectId: objectId,
			createdBy: loggedInUser?.userId,
			relatedParentObjectType: getRelatedParentObjectType(),
			relatedParentObjectId:
				moduleType === REQUIREMENT_INVENTORY ? groupId : citationId,
			grammarScore: fiveWData?.grammarScore.toString(),
			fiveWDescription: fiveWData?.fiveWResponse,
			taskType: TASKTYPE.TASK,
			moduleName: getModuleName(),
		};
		return createTaskData;
	};

	const getResponse = async () => {
		const isCip = process.env.REACT_APP_IS_CIP === 'true';
		let response;
		if (selectedTask?.id) {
			const taskData = updateTaskData();
			const callUpdateUsingCIP =
				isCip &&
				(taskData.status === 'Ready for Review' ||
					taskData.status === 'Complete');
			response = callUpdateUsingCIP
				? await updateTaskUsingCIP(taskData, citationId)
				: await updateTask(taskData, selectedTask.id);
		} else {
			const taskData = createTaskData();
			response = isCip
				? await createTaskUsingCIP(taskData, citationId)
				: await createTask([taskData]);
		}
		return response;
	};

	/**
	 * On click of Save for task call create/update method
	 */
	const onSaveHandler = async () => {
		if (isSaving) return;
		setIsSaving(true);
		const response: any = await getResponse();
		if (response) {
			if (response.statusCode === OK) {
				dispatch(
					setToastMessage({
						message: 'The Task has been successfully created/updated',
						severity: 'success',
						isToastOpen: true,
					}),
				);
				dispatch(setMappingDrawer(!sideDrawerState));
				dispatch(setTaxonomyDetailsTabIndex(OVERVIEW_TAB_LABEL));
				dispatch(setTabIndex('overview'));
				dispatch(setGrammarCheckCompleted(false));
				if (moduleType === HORIZON_SCAN_AND_ALERTS) {
					dispatch(setShouldHorizonDetailRefresh(true));
				}
			}
			setIsSaving(false);
			if (
				response.statusCode === BAD_REQUEST ||
				response.statusCode === NOT_FOUND
			) {
				if (response?.errorBody || response?.error) {
					const { errorBody } = response;
					const responseError: any = response.error;
					const errorMsg =
						(errorBody?.errorMessage as string) ||
						(responseError?.message as string);
					dispatch(
						setToastMessage({
							message: errorMsg,
							severity: 'error',
							isToastOpen: true,
						}),
					);
				}
			}
		}
	};

	const clickHandler = (task: ICreateUpdateTask) => {
		taskStatusOptionsSet();
		reset({ ...task });
		setSelectedTask(task);
		dispatch(setGrammarCheckCompleted(false));
	};

	return (
		<Grid
			container
			mt={3}
			className={taskStyle['task-layout']}
			data-testid="task-layout"
		>
			{isLoading ? (
				<CircularProgress size={90} className={taskStyle['control-loader']} />
			) : (
				<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
					<Box
						className={taskStyle['task-list']}
						sx={scrollProps('x-axis')}
						data-testid="task-card-lisk"
					>
						{availableTask.map(
							(
								task: ICreateUpdateTask,
								index: number,
								arr: ICreateUpdateTask[],
							) => (
								<TaskCard
									key={`taskList-${task.id}`}
									task={task}
									taskNumber={arr.length - index}
									clickHandler={clickHandler}
								/>
							),
						)}
					</Box>
					{availableTask.length === 0 && (
						<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
							No open tasks found
						</Grid>
					)}
				</Grid>
			)}

			<Grid item xs={12} sm={12} md={12} xl={12} lg={12}>
				<form>
					<Grid container>
						<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
							<Controller
								name="taskName"
								control={control}
								rules={{
									required: {
										value: true,
										message: 'Please enter Task name',
									},
									validate: (value) => !!value.trim(),
								}}
								render={({
									field: { name, value, onChange },
									fieldState: { error },
								}) => (
									<TextFieldWithLabel
										label={toSentenceCase('Task Name')}
										name={name}
										id="task-name"
										placeholder="Enter the task name"
										value={value}
										inputProps={{
											'data-testid': 'task-name',
										}}
										disabled={readOnly}
										onChange={onChange}
										error={!!error}
										required
										helperText={error ? error.message : null}
										className={taskStyle['task-field-width']}
										wordLimit={70}
										positionClass={taskStyle['word-limit-position']}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
							<Controller
								name="taskDescription"
								control={control}
								rules={{
									required: {
										value: true,
										message: 'Please enter task description',
									},
									validate: (value) => !!value.trim(),
								}}
								render={({
									field: { name, value, onChange },
									fieldState: { error },
								}) => (
									<TextFieldWithLabel
										label={toSentenceCase('Task Description (To-Do)')}
										name={name}
										id="task-description"
										inputProps={{
											'data-testid': 'task-description',
										}}
										placeholder="Enter the task description"
										value={value}
										onChange={onChange}
										disabled={readOnly}
										multiLine
										required
										error={!!error}
										helperText={error ? error.message : null}
										className={taskStyle['task-field-width']}
									/>
								)}
							/>
						</Grid>

						{(grammarCheckCompleted ||
							(selectedTask.id && selectedTask.controlDescription)) && (
							<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
								<Controller
									name="controlDescription"
									control={control}
									render={({
										field: { name, value },
										fieldState: { error },
									}) => (
										<TextFieldWithLabel
											label={toSentenceCase('Control Description')}
											name={name}
											id="control-description"
											inputProps={{
												'data-testid': 'control-description',
											}}
											placeholder=""
											value={value}
											multiLine
											disabled={readOnly}
											error={!!error}
											helperText={error ? error.message : null}
											className={taskStyle['task-field-width']}
										/>
									)}
								/>
							</Grid>
						)}

						{(grammarCheckCompleted ||
							(selectedTask.id && selectedTask.controlDescription)) && (
							<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
								<Box className={taskStyle['grammar-check-box']}>
									<div className={taskStyle['control-description-header']}>
										Control quality and grammar check
									</div>
									<Stack direction="column" sx={{ padding: '10px' }}>
										<GrammarCheck data={fiveWData} hideActionButton />
									</Stack>
								</Box>
							</Grid>
						)}
						<Grid item xs={6} sm={6} md={6} xl={6} lg={6} pl={1}>
							<Controller
								name="startDate"
								control={control}
								render={({ field: { name, value } }) => (
									<DatepickerComponent
										open={false}
										propType={{
											name,
											label: toSentenceCase('Created Date'),
											disabled: true,
											required: true,
											className: taskStyle['task-created-date'],
										}}
										dateValue={value}
										readOnly
									/>
								)}
							/>
						</Grid>
						<Grid item xs={6} sm={6} md={6} xl={6} lg={6} pr={1}>
							<Controller
								name="dueDate"
								control={control}
								rules={{
									required: {
										value: true,
										message: 'please enter DueDate',
									},
								}}
								render={({ field: { name, value } }) => (
									<DatepickerComponent
										propType={{
											label: toSentenceCase('Due Date'),
											disablePast: true,
											disabled: readOnly,
											name: { name },
											id: 'dueDate',
											required: true,
											className: taskStyle['task-field-width'],
											inputProps: {
												'data-testid': 'task-dueDate',
											},
										}}
										onChange={onDateChangeHandler(name)}
										dateValue={getDateStringFromISOString(value)}
										readOnly
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
							<Controller
								name="priorityLevel"
								control={control}
								render={({ field: { name, value, onChange } }) => (
									<SelectDropdown
										label={toSentenceCase('Importance Level')}
										name={name}
										id="task-priority-level"
										options={risklevel}
										mode="standard"
										inputProps={{
											'data-testid': 'task-level',
										}}
										disabled={readOnly}
										clearable
										value={value}
										onChange={onChange}
										className={taskStyle['task-field-dropdown-level']}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
							<Controller
								name="taskOwner"
								control={control}
								rules={{
									required: {
										value: true,
										message: 'please select Task owner',
									},
								}}
								render={({
									field: { name, value, onChange },
									fieldState: { error },
								}) => (
									<SelectDropdown
										label={toSentenceCase('Task Owner')}
										name={name}
										required
										id="task-owner"
										options={taskUsers}
										mode="standard"
										inputProps={{
											'data-testid': 'task-owner',
										}}
										clearable
										value={value}
										onChange={onChange}
										disabled={readOnly}
										error={!!error}
										helperText={error ? error.message : null}
										className={taskStyle['task-field-dropdown']}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
							<Controller
								name="assignedUserId"
								control={control}
								rules={{
									required: {
										value: true,
										message: 'please select Assigned to',
									},
								}}
								render={({
									field: { name, value, onChange },
									fieldState: { error },
								}) => (
									<SelectDropdown
										label="Assigned to"
										name={name}
										required
										id="task-assingedTo"
										options={taskUsers}
										inputProps={{
											'data-testid': 'task-AssignedTo',
										}}
										mode="standard"
										clearable
										value={value}
										onChange={onChange}
										disabled={readOnly}
										error={!!error}
										helperText={error ? error.message : null}
										className={taskStyle['task-field-dropdown']}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
							<Controller
								name="status"
								control={control}
								render={({ field: { name, value, onChange } }) => (
									<SelectDropdown
										label="Status"
										name={name}
										id="task-status"
										mode="standard"
										options={statusOptions}
										inputProps={{
											'data-testid': 'task-status',
										}}
										clearable
										value={value}
										disabled={readOnly}
										onChange={onChange}
										className={taskStyle['task-field-dropdown']}
									/>
								)}
							/>
						</Grid>
					</Grid>
				</form>
			</Grid>

			{selectedTask?.id && (
				<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
					<AccordionComp
						expanded={expanded === 'comments'}
						data-testid="task-comment-accordion"
						onChange={handleAccordionChange('comments')}
						summary={
							<Stack direction="row">
								<Typography className={taskStyle['font-weight-bold']}>
									Comments
								</Typography>
								<img
									src={infoIcon}
									alt="info"
									className={taskStyle['comments-info']}
								/>
							</Stack>
						}
						details={
							<TaskComment taskId={selectedTask?.id || ''} type="normal" />
						}
					/>
				</Grid>
			)}

			<Grid item xs={12} sm={12} md={12} xl={12} lg={12} p={1}>
				<Stack direction="row" className={taskStyle['btn-task-button']}>
					<div>
						<ButtonComp
							btnType="secondary"
							clickHandler={onCancelHandler}
							data-testid="cancel-btn"
						>
							Cancel
						</ButtonComp>
					</div>
					<div>
						<ButtonComp
							btnType="primary"
							clickHandler={onSaveHandler}
							disabled={!isDirty || !isValid}
							data-testid="save-btn"
							compClass={taskStyle['btn-save']}
						>
							Save
						</ButtonComp>
					</div>
				</Stack>
			</Grid>
		</Grid>
	);
};
export default Tasks;
